import type { TFunction } from "i18next";
import { ProviderName } from "../shared/ProviderName";
import type { RequestSummary } from "../../api/requestsAdmin";
import { CopyEmailButton } from "./CopyEmailButton";
import {
  formatRelative,
  isChanged,
  shortEmail,
  typeLabel,
} from "./requestHistoryFormat";

/** A request, as one approver reads it at a glance: a title that says what is asked, and a line of supporting facts. */
export interface DecisionHeadline {
  title: React.JSX.Element;
  subtitle: string;
}

const wholeDollars = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

/** Reads a string field from a request's payload under either its camel-case or Pascal-case name. */
function payloadField(payloadJson: string, name: string): string {
  try {
    const payload = JSON.parse(payloadJson) as Record<string, unknown>;
    const value =
      payload[name] ?? payload[name.charAt(0).toUpperCase() + name.slice(1)];
    return typeof value === "string" ? value : "";
  } catch {
    return "";
  }
}

/** The person a request is about, as an approver names them: their name, or their address without the shared domain. */
export function requesterName(request: RequestSummary): string {
  const name = request.personDisplayName?.trim() ?? "";
  return name !== ""
    ? name
    : (shortEmail(request.personEmail) ?? request.personId);
}

/** A person's name in a title, with a button beside it that copies their full email address when the request records one. */
function person(request: RequestSummary): React.JSX.Element {
  return (
    <>
      <strong>{requesterName(request)}</strong>
      {request.personEmail && <CopyEmailButton email={request.personEmail} />}
    </>
  );
}

/** Joins the parts of a supporting line, leaving out any that are empty. */
function line(...parts: string[]): string {
  return parts.filter((part) => part !== "").join(" · ");
}

/**
 * What an approver needs to decide at a glance. A manual vendor task reads "Remove [Cursor] for hugh.bierrum"; a request to join a team
 * "hugh.bierrum wants to join Developers"; an allowance request names the person, the kind of change and what it changes. The supporting
 * line holds how long ago it was raised and, for a task or a team, why. Everything else is in the request's detail.
 *
 * @param request - The request.
 * @param t - The translation function.
 */
export function decisionHeadline(
  request: RequestSummary,
  t: TFunction,
): DecisionHeadline {
  const age = formatRelative(request.createdAt);

  if (request.requestType === "ManualVendorAction") {
    const vendorId = payloadField(request.payloadJson, "vendorId");
    const verb = payloadField(request.payloadJson, "action").replace(
      /\s+access$/i,
      "",
    );
    return {
      title: (
        <>
          <strong>
            {verb === "" ? t("myRequests.decision.task", "Set up") : verb}
          </strong>{" "}
          {vendorId !== "" && (
            <ProviderName
              vendorId={vendorId}
              name={t(
                `admin.entitlement.person.allocation.vendors.${vendorId}`,
                vendorId,
              )}
            />
          )}{" "}
          {t("myRequests.decision.for", "for")} {person(request)}
        </>
      ),
      subtitle: line(age, payloadField(request.payloadJson, "reason")),
    };
  }

  if (request.requestType === "JoinGroup") {
    return {
      title: (
        <>
          {person(request)}{" "}
          {t("myRequests.decision.wantsToJoin", "wants to join")}{" "}
          <strong>{payloadField(request.payloadJson, "groupName")}</strong>
        </>
      ),
      subtitle: line(age, payloadField(request.payloadJson, "reason")),
    };
  }

  const summary = (request.providerChanges ?? [])
    .filter(isChanged)
    .map(
      (change) =>
        `${request.providerNames?.[change.vendorId] ?? change.vendorId} ${change.afterEnabled ? wholeDollars.format(change.afterAmountUsd) : t("myRequests.decision.off", "off")}`,
    )
    .join(", ");
  return {
    title: (
      <>
        {person(request)} · {typeLabel(request.requestType)}
      </>
    ),
    subtitle: line(age, summary),
  };
}
